import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCashRegisters,
  createCashRegister,
  openCashShift,
  closeCashShift,
  getCashShifts,
  createCashTransaction,
  getCashShift,
} from '../services/api';
import Icon from '../components/Icon';
import HelpIcon from '../components/help/HelpIcon';
import CashTransactionModal from '../components/CashTransactionModal';
import CashCountModal from '../components/CashCountModal';
import { trackEvent } from '../utils/analytics';
import { getDateLocale } from '../utils/dateLocale';
import { storage } from '../utils/storage';
import './CashRegisterPage.css';

function CashRegisterPage() {
  const { t, i18n } = useTranslation();

  // State
  const [registers, setRegisters] = useState([]);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [currentShift, setCurrentShift] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Shift history controls
  const [historyPage, setHistoryPage] = useState(1);
  const [historySortField, setHistorySortField] = useState('openedAt');
  const [historySortDir, setHistorySortDir] = useState('desc');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const HISTORY_PAGE_SIZE = 10;

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionType, setTransactionType] = useState('IN');

  // Form states
  const [newRegisterName, setNewRegisterName] = useState('');
  const [registerNameTouched, setRegisterNameTouched] = useState(false);
  const [openingAmount, setOpeningAmount] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [closingAmount, setClosingAmount] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [closeNotes, setCloseNotes] = useState('');

  // Load registers
  const loadRegisters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCashRegisters();
      setRegisters(response.registers || []);

      // Auto-select first register if none selected
      if (!selectedRegister && response.registers?.length > 0) {
        setSelectedRegister(response.registers[0]);
      }
    } catch (err) {
      console.error('Error loading registers:', err);
      setError(t('cashRegister.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedRegister, t]);

  // Load shifts for selected register
  const loadShifts = useCallback(async () => {
    if (!selectedRegister) return;

    try {
      const response = await getCashShifts(selectedRegister.registerId);
      const allShifts = response.shifts || [];
      setShifts(allShifts);

      // Find current open shift
      const openShift = allShifts.find((s) => s.status === 'open');

      if (openShift) {
        // Fetch full details including transactions
        const fullShift = await getCashShift(selectedRegister.registerId, openShift.shiftId);
        setCurrentShift(fullShift);
      } else {
        setCurrentShift(null);
      }
    } catch (err) {
      console.error('Error loading shifts:', err);
    }
  }, [selectedRegister]);

  useEffect(() => {
    loadRegisters();
  }, [loadRegisters]);

  useEffect(() => {
    if (selectedRegister) {
      loadShifts();
    }
  }, [selectedRegister, loadShifts]);

  // Create new register
  const handleCreateRegister = async () => {
    if (!newRegisterName.trim()) return;

    try {
      await createCashRegister({ name: newRegisterName.trim() });
      setNewRegisterName('');
      setRegisterNameTouched(false);
      setShowCreateModal(false);
      loadRegisters();
    } catch (err) {
      console.error('Error creating register:', err);
      setError(t('cashRegister.messages.createError'));
    }
  };

  // Open shift
  const handleOpenShift = async () => {
    if (!selectedRegister || !openingAmount) return;

    try {
      await openCashShift(selectedRegister.registerId, parseFloat(openingAmount));
      trackEvent('shift_opened', {
        restaurant_id: storage.get('selectedRestaurantId'),
        organization_id: storage.get('selectedOrganizationId'),
        register_id: selectedRegister.registerId,
        opening_amount: parseFloat(openingAmount),
      });
      setOpeningAmount('');
      setShowOpenModal(false);
      loadShifts();
      window.dispatchEvent(new Event('cashShiftChanged'));
    } catch (err) {
      console.error('Error opening shift:', err);
      setError(t('cashRegister.messages.openError'));
    }
  };

  // Close shift
  const handleCloseShift = async (data) => {
    if (!selectedRegister || !currentShift) return;

    const { closingAmount, cashCount, notes } = data;

    try {
      await closeCashShift(
        selectedRegister.registerId,
        currentShift.shiftId,
        parseFloat(closingAmount),
        notes || null,
        cashCount
      );
      setShowCloseModal(false);
      loadShifts();
      window.dispatchEvent(new Event('cashShiftChanged'));
    } catch (err) {
      console.error('Error closing shift:', err);
      setError(t('cashRegister.messages.closeError'));
    }
  };

  // Handle Transaction
  const handleTransaction = async (data) => {
    if (!selectedRegister || !currentShift) return;

    try {
      await createCashTransaction(selectedRegister.registerId, currentShift.shiftId, data);
      setShowTransactionModal(false);
      loadShifts(); // Reload to get updated totals and transactions
      window.dispatchEvent(new Event('cashShiftChanged'));
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(t('cashRegister.messages.transactionError'));
    }
  };

  const openTransactionModal = (type) => {
    setTransactionType(type);
    setShowTransactionModal(true);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(getDateLocale(i18n.language), {
      style: 'currency',
      currency: 'ARS',
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString(getDateLocale(i18n.language), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Reset page when filters change
  useEffect(() => {
    setHistoryPage(1);
  }, [historyDateFrom, historyDateTo, historySortField, historySortDir]);

  // Filtered, sorted, paginated closed shifts
  const closedShifts = useMemo(() => shifts.filter((s) => s.status === 'closed'), [shifts]);

  const filteredShifts = useMemo(() => {
    let result = closedShifts;
    if (historyDateFrom) {
      const from = new Date(historyDateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter((s) => new Date(s.openedAt) >= from);
    }
    if (historyDateTo) {
      const to = new Date(historyDateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter((s) => new Date(s.openedAt) <= to);
    }
    return result;
  }, [closedShifts, historyDateFrom, historyDateTo]);

  const sortedShifts = useMemo(() => {
    const sorted = [...filteredShifts];
    sorted.sort((a, b) => {
      let valA, valB;
      switch (historySortField) {
        case 'openedAt':
        case 'closedAt':
          valA = new Date(a[historySortField] || 0).getTime();
          valB = new Date(b[historySortField] || 0).getTime();
          break;
        case 'openingAmount':
        case 'totalSales':
        case 'closingAmount':
          valA = a[historySortField] || 0;
          valB = b[historySortField] || 0;
          break;
        case 'difference':
          valA = a.difference || (a.closingAmount || 0) - (a.expectedAmount || a.openingAmount);
          valB = b.difference || (b.closingAmount || 0) - (b.expectedAmount || b.openingAmount);
          break;
        default:
          valA = 0;
          valB = 0;
      }
      return historySortDir === 'asc' ? valA - valB : valB - valA;
    });
    return sorted;
  }, [filteredShifts, historySortField, historySortDir]);

  const historyTotalPages = Math.max(1, Math.ceil(sortedShifts.length / HISTORY_PAGE_SIZE));
  const paginatedShifts = useMemo(
    () =>
      sortedShifts.slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE),
    [sortedShifts, historyPage]
  );

  const handleHistorySort = (field) => {
    if (historySortField === field) {
      setHistorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setHistorySortField(field);
      setHistorySortDir('desc');
    }
  };

  const handleClearDateFilter = () => {
    setHistoryDateFrom('');
    setHistoryDateTo('');
  };

  // Calculate totals for current shift display
  const calculateCurrentTotals = () => {
    if (!currentShift) return { in: 0, out: 0, expected: 0 };

    // If we have transactions from the API
    const transactions = currentShift.transactions || [];
    const totalIn = transactions
      .filter((t) => t.type === 'IN')
      .reduce((sum, t) => sum + t.amount, 0);
    const totalOut = transactions
      .filter((t) => t.type === 'OUT')
      .reduce((sum, t) => sum + t.amount, 0);

    // Note: expectedAmount from API might not include real-time sales if we don't poll
    // But for now we rely on what getCashShift returns (which should include sales if implemented)
    // OR we calculate locally: opening + sales (if available) + in - out

    // For now, let's use the simple calculation:
    // expected = opening + (sales?) + in - out
    // Since we don't have real-time sales in the frontend without polling,
    // we might just show Opening + In - Out as "Cash in Drawer (excluding sales)"
    // or if the backend provides 'totalSales', we use it.

    // The backend getCashShift currently returns the shift object.
    // It does NOT calculate sales on the fly in the GET endpoint (it was a TODO).
    // So 'totalSales' might be 0 until closed.
    // We will show "Opening + In - Out" as the base cash.

    const baseExpected = (currentShift.openingAmount || 0) + totalIn - totalOut;

    // Tips from backend (if available in shift object) or calculate from orders if we had them
    // Since getCashShift returns the shift object which now includes totalTips and totalTipsCash (after close),
    // but for OPEN shifts, we might need to rely on what the backend sends.
    // The backend 'getCashShift' handler returns the shift item.
    // The shift item in DB does NOT have totalTips updated in real-time unless we update it.
    // However, 'closeCashShift' calculates it at the end.
    // If we want to see it LIVE, we need 'getCashShift' to calculate it on the fly like it does for sales?
    // Let's check getCashShift.js.
    // It returns the item. It does NOT calculate on the fly.
    // So for an OPEN shift, we won't see tips unless we implement a way to fetch them.
    // But wait, 'closeCashShift' calculates it.
    // For the MVP, maybe it's acceptable to only see the final tip amount when closing?
    // Or we can add a 'refresh' that calls an endpoint to get current stats.
    // Actually, 'getCashShift' should probably return the calculated values for open shifts too.
    // Let's check getCashShift.js again.

    return {
      totalIn,
      totalOut,
      baseExpected,
      totalTips: currentShift.totalTips || 0,
      totalTipsCash: currentShift.totalTipsCash || 0,
    };
  };

  const { totalIn, totalOut, baseExpected, totalTips, totalTipsCash } = calculateCurrentTotals();

  if (loading && registers.length === 0) {
    return (
      <div className="cash-register-page">
        <div className="loading-container">
          <Icon name="hourglass_empty" className="loading-icon spin" />
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cash-register-page">
      <div className="page-header">
        <h1>
          {t('cashRegister.title')} <HelpIcon topicKey="cashRegister.overview" />
        </h1>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShowCreateModal(true)}
        >
          <Icon name="add" />
          {t('cashRegister.createRegister')}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <Icon name="error" />
          {error}
          <button type="button" onClick={() => setError(null)}>
            <Icon name="close" />
          </button>
        </div>
      )}

      <div className="cash-register-layout">
        {/* Register selector */}
        <div className="register-selector">
          <h2>{t('cashRegister.selectRegister')}</h2>
          <div className="register-list">
            {registers.length === 0 ? (
              <p className="no-registers">{t('cashRegister.noRegisters')}</p>
            ) : (
              registers.map((register) => (
                <div
                  key={register.registerId}
                  className={`register-card ${selectedRegister?.registerId === register.registerId ? 'selected' : ''}`}
                  onClick={() => setSelectedRegister(register)}
                >
                  <Icon name="point_of_sale" className="register-icon" />
                  <div className="register-info">
                    <span className="register-name">{register.name}</span>
                    <span
                      className={`register-status ${register.hasOpenShift ? 'open' : 'closed'}`}
                    >
                      {register.hasOpenShift ? t('cashRegister.open') : t('cashRegister.closed')}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="register-content">
          {selectedRegister ? (
            <>
              {/* Current shift status */}
              <div className="shift-status-card">
                <h2>
                  {t('cashRegister.currentShift')} <HelpIcon topicKey="cashRegister.shift" />
                </h2>
                {currentShift ? (
                  <div className="shift-details">
                    <div className="shift-header-info">
                      <div className="shift-info-row">
                        <span className="label">{t('cashRegister.status')}:</span>
                        <span
                          className={`cash-status-badge ${currentShift.status === 'closed' ? 'cash-status-badge--closed' : 'cash-status-badge--open'}`}
                        >
                          {currentShift.status === 'closed'
                            ? t('cashRegister.shiftClosedStatus')
                            : t('cashRegister.shiftOpen')}
                        </span>
                      </div>
                      <div className="shift-info-row">
                        <span className="label">{t('cashRegister.openedAt')}:</span>
                        <span className="value">{formatDate(currentShift.openedAt)}</span>
                      </div>
                      <div className="shift-info-row">
                        <span className="label">{t('cashRegister.openedBy')}:</span>
                        <span className="value">{currentShift.openedBy || '-'}</span>
                      </div>
                    </div>

                    <div className="shift-financials">
                      <div className="financial-item">
                        <span className="label">{t('cashRegister.openingAmount')}</span>
                        <span className="value">{formatCurrency(currentShift.openingAmount)}</span>
                      </div>
                      <div className="financial-item income">
                        <span className="label">{t('cashRegister.income')}</span>
                        <span className="value">+{formatCurrency(totalIn)}</span>
                      </div>
                      <div className="financial-item expense">
                        <span className="label">{t('cashRegister.expense')}</span>
                        <span className="value">-{formatCurrency(totalOut)}</span>
                      </div>
                      {/* Tips Display (Only if closed or if we implement live fetching) */}
                      {currentShift.status === 'closed' && (
                        <div className="financial-item">
                          <span className="label">{t('cashRegister.tips')}</span>
                          <span className="value">{formatCurrency(totalTips)}</span>
                          <small>
                            ({t('payment.cash')}: {formatCurrency(totalTipsCash)})
                          </small>
                        </div>
                      )}
                      <div className="financial-item total">
                        <span className="label">{t('cashRegister.partialTotal')}</span>
                        <span className="value">{formatCurrency(baseExpected)}</span>
                        <small className="hint">({t('cashRegister.excludingSales')})</small>
                      </div>
                    </div>

                    <div className="shift-actions">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => openTransactionModal('IN')}
                      >
                        <Icon name="add_circle" />
                        {t('cashRegister.addIncome')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => openTransactionModal('OUT')}
                      >
                        <Icon name="remove_circle" />
                        {t('cashRegister.addExpense')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setShowCloseModal(true)}
                      >
                        <Icon name="lock" />
                        {t('cashRegister.closeShift')}
                      </button>
                    </div>

                    {/* Transactions List */}
                    {currentShift.transactions && currentShift.transactions.length > 0 && (
                      <div className="current-transactions">
                        <h4>{t('cashRegister.movements')}</h4>
                        <table className="transactions-table">
                          <tbody>
                            {currentShift.transactions.map((tx) => (
                              <tr key={tx.transactionId}>
                                <td>{new Date(tx.createdAt).toLocaleTimeString()}</td>
                                <td>{tx.reason}</td>
                                <td className={tx.type === 'IN' ? 'positive' : 'negative'}>
                                  {tx.type === 'IN' ? '+' : '-'}
                                  {formatCurrency(tx.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-shift">
                    <Icon name="lock_open" className="no-shift-icon" />
                    <p>{t('cashRegister.noShiftOpen')}</p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowOpenModal(true)}
                    >
                      <Icon name="lock_open" />
                      {t('cashRegister.openShift')}
                    </button>
                  </div>
                )}
              </div>

              {/* Shift history */}
              <div className="shift-history">
                <h2>{t('cashRegister.shiftHistory')}</h2>

                {/* Date filter */}
                <div className="history-filters">
                  <div className="history-date-filter">
                    <label>{t('cashRegister.history.dateFrom')}</label>
                    <input
                      type="date"
                      value={historyDateFrom}
                      onChange={(e) => setHistoryDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="history-date-filter">
                    <label>{t('cashRegister.history.dateTo')}</label>
                    <input
                      type="date"
                      value={historyDateTo}
                      onChange={(e) => setHistoryDateTo(e.target.value)}
                    />
                  </div>
                  {(historyDateFrom || historyDateTo) && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={handleClearDateFilter}
                    >
                      {t('cashRegister.history.clearFilter')}
                    </button>
                  )}
                </div>

                {closedShifts.length === 0 ? (
                  <p className="no-history">{t('cashRegister.noHistory')}</p>
                ) : filteredShifts.length === 0 ? (
                  <p className="no-history">{t('cashRegister.history.noResults')}</p>
                ) : (
                  <>
                    <table className="shifts-table">
                      <thead>
                        <tr>
                          {[
                            { field: 'openedAt', label: t('cashRegister.openedAt') },
                            { field: 'closedAt', label: t('cashRegister.closedAt') },
                            { field: 'openingAmount', label: t('cashRegister.openingAmount') },
                            { field: 'totalSales', label: t('cashRegister.totalSales') },
                            { field: 'closingAmount', label: t('cashRegister.closingAmount') },
                            { field: 'difference', label: t('cashRegister.difference') },
                          ].map(({ field, label }) => (
                            <th
                              key={field}
                              className="sortable-th"
                              onClick={() => handleHistorySort(field)}
                            >
                              {label}
                              <span className="sort-indicator">
                                {historySortField === field
                                  ? historySortDir === 'asc'
                                    ? ' ▲'
                                    : ' ▼'
                                  : ''}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedShifts.map((shift) => {
                          const diff =
                            shift.difference ||
                            (shift.closingAmount || 0) -
                              (shift.expectedAmount || shift.openingAmount);
                          return (
                            <tr key={shift.shiftId}>
                              <td>{formatDate(shift.openedAt)}</td>
                              <td>{formatDate(shift.closedAt)}</td>
                              <td>{formatCurrency(shift.openingAmount)}</td>
                              <td className="sales-amount">
                                {formatCurrency(shift.totalSales || 0)}
                              </td>
                              <td>{formatCurrency(shift.closingAmount)}</td>
                              <td className={diff >= 0 ? 'positive' : 'negative'}>
                                {diff >= 0 ? '+' : ''}
                                {formatCurrency(diff)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {historyTotalPages > 1 && (
                      <div className="pagination-controls">
                        <span className="pagination-info">
                          {t('cashRegister.history.showing', {
                            from: (historyPage - 1) * HISTORY_PAGE_SIZE + 1,
                            to: Math.min(historyPage * HISTORY_PAGE_SIZE, sortedShifts.length),
                            total: sortedShifts.length,
                          })}
                        </span>
                        <div className="pagination-buttons">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setHistoryPage(1)}
                            disabled={historyPage === 1}
                          >
                            «
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setHistoryPage((p) => p - 1)}
                            disabled={historyPage === 1}
                          >
                            ‹
                          </button>
                          {Array.from({ length: historyTotalPages }, (_, i) => i + 1)
                            .filter((page) => {
                              if (historyTotalPages <= 7) return true;
                              if (page === 1 || page === historyTotalPages) return true;
                              if (Math.abs(page - historyPage) <= 1) return true;
                              return false;
                            })
                            .reduce((acc, page, idx, arr) => {
                              if (idx > 0 && page - arr[idx - 1] > 1) {
                                acc.push('ellipsis-' + page);
                              }
                              acc.push(page);
                              return acc;
                            }, [])
                            .map((item) =>
                              typeof item === 'string' ? (
                                <span key={item} className="pagination-ellipsis">
                                  …
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  key={item}
                                  className={`btn btn-sm ${item === historyPage ? 'btn-primary' : 'btn-secondary'}`}
                                  onClick={() => setHistoryPage(item)}
                                >
                                  {item}
                                </button>
                              )
                            )}
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setHistoryPage((p) => p + 1)}
                            disabled={historyPage === historyTotalPages}
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setHistoryPage(historyTotalPages)}
                            disabled={historyPage === historyTotalPages}
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="no-register-selected">
              <Icon name="point_of_sale" className="big-icon" />
              <p>{t('cashRegister.selectRegisterPrompt')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Register Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowCreateModal(false);
            setRegisterNameTouched(false);
          }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('cashRegister.createRegister')}</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setRegisterNameTouched(false);
                }}
              >
                <Icon name="close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="new-register-name">
                  {t('cashRegister.registerName')} <span className="required">*</span>
                </label>
                <input
                  id="new-register-name"
                  type="text"
                  required
                  value={newRegisterName}
                  onChange={(e) => setNewRegisterName(e.target.value)}
                  onBlur={() => setRegisterNameTouched(true)}
                  placeholder={t('cashRegister.registerNamePlaceholder')}
                  className={registerNameTouched && !newRegisterName.trim() ? 'input-error' : ''}
                  autoFocus
                />
                {registerNameTouched && !newRegisterName.trim() && (
                  <span className="field-error">
                    {t('cashRegister.validation.registerNameRequired')}
                  </span>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setShowCreateModal(false);
                  setRegisterNameTouched(false);
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateRegister}
                disabled={!newRegisterName.trim()}
              >
                {t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open Shift Modal */}
      {showOpenModal && (
        <div className="modal-overlay" onClick={() => setShowOpenModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('cashRegister.openShiftModal.title')}</h2>
              <button type="button" className="close-btn" onClick={() => setShowOpenModal(false)}>
                <Icon name="close" />
              </button>
            </div>
            <div className="modal-body">
              <p>{t('cashRegister.openShiftModal.enterAmount')}</p>
              <div className="form-group">
                <label htmlFor="opening-amount">{t('cashRegister.openingAmount')}</label>
                <input
                  id="opening-amount"
                  type="number"
                  value={openingAmount}
                  onChange={(e) => setOpeningAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowOpenModal(false)}
              >
                {t('cashRegister.openShiftModal.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleOpenShift}
                disabled={!openingAmount || parseFloat(openingAmount) < 0}
              >
                {t('cashRegister.openShiftModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Shift Modal (Cash Count) */}
      {showCloseModal && (
        <CashCountModal
          expectedAmount={baseExpected}
          onClose={() => setShowCloseModal(false)}
          onConfirm={handleCloseShift}
        />
      )}

      {/* Transaction Modal */}
      {showTransactionModal && (
        <CashTransactionModal
          type={transactionType}
          onClose={() => setShowTransactionModal(false)}
          onConfirm={handleTransaction}
        />
      )}
    </div>
  );
}

export default CashRegisterPage;
